import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activateAccount,
  forgotPassword,
  login,
  logout,
  resetPassword,
  revokeAccount,
} from '../api/authApi';
import { CurrentUser } from '../api/memberApi/interfaces';
import { getCurrentMember, updateMyLocale } from '../api/memberApi';
import { MemberLocale } from '../api/memberApi/interfaces';
import { AxiosError } from 'axios';
import {
  IActivateAccountParams,
  IForgotPasswordParams,
  IResetPasswordParams,
  IRevokeAccountParams,
} from '@api/authApi/interfaces';

export const CURRENT_USER_QUERY_KEY = ['current-user'] as const;

export const useGetCurrentMember = () => {
  return useQuery<CurrentUser>({
    queryFn: getCurrentMember,
    queryKey: CURRENT_USER_QUERY_KEY,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: login,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CURRENT_USER_QUERY_KEY,
      });
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(CURRENT_USER_QUERY_KEY, null);
    },
  });
};

export const useActivateAccountMutation = () => {
  return useMutation<string, AxiosError, IActivateAccountParams>({
    mutationFn: activateAccount,
  });
};

export const useRevokeAccountMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<string, AxiosError, IRevokeAccountParams>({
    mutationFn: revokeAccount,
    onSuccess: (_data, params) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-member', params.memberSlug],
      });
    },
  });
};

export const useUpdateMyLocaleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locale: MemberLocale | null) => updateMyLocale(locale),
    onSuccess: (_data, locale) => {
      queryClient.setQueryData<CurrentUser | undefined>(
        CURRENT_USER_QUERY_KEY,
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            member: {
              ...current.member,
              locale,
            },
          };
        }
      );
    },
  });
};

export const useForgotPassword = () => {
  return useMutation<string, AxiosError, IForgotPasswordParams>({
    mutationFn: forgotPassword,
  });
};

export const useResetPassword = () => {
  return useMutation<string, AxiosError, IResetPasswordParams>({
    mutationFn: resetPassword,
  });
};
